import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const JEWELLERY_TYPES = [
  "Necklace", "Ring", "Earring", "Bangle", "Bracelet", "Pendant",
  "Chain", "Mangalsutra", "Nose Pin", "Anklet", "Kadli", "Other",
];
const METAL_TYPES = [
  "22 CT Gold",
  "18 CT Rose Gold",
  "18 CT Yellow Gold",
];

export default function OrderCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    customer_id: params.get("customer_id") || "",
    title: "",
    description: "",
    modification_instructions: "",
    source_phone: "",
    expected_delivery: "",
    priority: "Medium",
    image_paths: [],
    jewellery_type: "",
    metal_type: "",
    size_dimensions: "",
    finish_type: "",
    karigar_notes: "",
    estimated_weight: 0, // approx weight guidance only
    final_weight: 0,     // production weight (filled when order is done)
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  useEffect(() => {
    if (form.customer_id) {
      const c = customers.find((c) => c.id === form.customer_id);
      if (c && !form.source_phone) {
        setForm((f) => ({ ...f, source_phone: c.primary_phone }));
      }
    }
    // eslint-disable-next-line
  }, [form.customer_id, customers]);

  const onUpload = async (files) => {
    setUploading(true);
    const newPaths = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPaths.push(data.path);
      } catch {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setForm((f) => ({ ...f, image_paths: [...f.image_paths, ...newPaths] }));
    setUploading(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        expected_delivery: form.expected_delivery ? new Date(form.expected_delivery).toISOString() : null,
        estimated_weight: parseFloat(form.estimated_weight) || 0,
        final_weight: parseFloat(form.final_weight) || 0,
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_no} created`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create order");
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <Link to="/orders" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Orders
      </Link>
      <div>
        <div className="overline text-zinc-500">Create</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">New Custom Order</h1>
      </div>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="order-create-form">
        {/* Reference Images */}
        <Card className="p-6 space-y-5">
          <div>
            <h3 className="font-heading font-bold text-xl">Reference Design</h3>
            <p className="text-xs text-zinc-500 mt-1">Upload one or more images. These are shared on the customer & karigar copies.</p>
          </div>
          <div>
            <input
              id="img-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onUpload(Array.from(e.target.files || []))}
            />
            <label
              htmlFor="img-input"
              className="border-2 border-dashed border-border rounded-md p-8 grid place-items-center text-center cursor-pointer hover:border-zinc-900 transition-colors"
              data-testid="image-upload-area"
            >
              {uploading ? (
                <div className="flex items-center text-zinc-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</div>
              ) : (
                <div className="text-sm text-zinc-600">
                  <Upload className="w-5 h-5 mx-auto mb-2 text-zinc-400" />
                  <span className="font-medium">Click to upload reference designs</span>
                  <div className="text-xs text-zinc-500 mt-1">JPG, PNG, WebP — multiple allowed</div>
                </div>
              )}
            </label>
            {form.image_paths.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="uploaded-images">
                {form.image_paths.map((p, i) => (
                  <div key={p} className="relative aspect-square rounded-md overflow-hidden border border-border bg-zinc-100">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-zinc-900/80 text-white grid place-items-center hover:bg-red-600"
                      onClick={() => setForm((f) => ({ ...f, image_paths: f.image_paths.filter((_, j) => j !== i) }))}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Order Details */}
        <Card className="p-6 space-y-5">
          <h3 className="font-heading font-bold text-xl">Order Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Order Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Antique gold haar — modified" data-testid="order-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.shop_name ? `· ${c.shop_name}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Source Phone *</Label>
              <Input required value={form.source_phone} onChange={(e) => setForm({ ...form, source_phone: e.target.value })} placeholder="Number used for this order" data-testid="order-source-phone" />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="order-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Expected Delivery</Label>
              <Input type="datetime-local" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} data-testid="order-delivery-input" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Customer brief in plain words…" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Modification Instructions</Label>
              <Textarea rows={3} value={form.modification_instructions} onChange={(e) => setForm({ ...form, modification_instructions: e.target.value })} placeholder="e.g. lighter weight, smaller stones, change clasp…" />
            </div>
          </div>
        </Card>

        {/* Crafting Instructions */}
        <Card className="p-6 space-y-5 bg-amber-50/40 border-amber-200">
          <div>
            <h3 className="font-heading font-bold text-xl">Crafting Instructions</h3>
            <p className="text-xs text-zinc-600 mt-1">Sent to karigar only. Will appear on the Karigar Copy of the Job Sheet.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <div className="space-y-2">
              <Label>Jewellery Type</Label>
              <Select value={form.jewellery_type} onValueChange={(v) => setForm({ ...form, jewellery_type: v })}>
                <SelectTrigger data-testid="order-jewellery-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {JEWELLERY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Metal Type / Carat</Label>
              <Select value={form.metal_type} onValueChange={(v) => setForm({ ...form, metal_type: v })}>
                <SelectTrigger data-testid="order-metal-type"><SelectValue placeholder="Select metal" /></SelectTrigger>
                <SelectContent>
                  {METAL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Approx Weight Guidance (g)</Label>
              <Input
                type="number" step="0.001"
                value={form.estimated_weight}
                onChange={(e) => setForm({ ...form, estimated_weight: e.target.value })}
                placeholder="optional"
                data-testid="order-approx-weight"
              />
            </div>
            <div className="space-y-2">
              <Label>Finish Type</Label>
              <Input value={form.finish_type} onChange={(e) => setForm({ ...form, finish_type: e.target.value })} placeholder="e.g. matte, polish, antique" data-testid="order-finish-type" />
            </div>
            <div className="space-y-2">
              <Label>Size / Dimensions</Label>
              <Input value={form.size_dimensions} onChange={(e) => setForm({ ...form, size_dimensions: e.target.value })} placeholder="e.g. 18 inch chain · ring size 14" data-testid="order-size-dim" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Karigar Notes / Special Instructions</Label>
              <Textarea
                rows={3}
                value={form.karigar_notes}
                onChange={(e) => setForm({ ...form, karigar_notes: e.target.value })}
                placeholder="Internal notes for karigar — not shown to customer"
                data-testid="order-karigar-notes"
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/orders")}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800" data-testid="order-create-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Order
          </Button>
        </div>
      </form>
    </div>
  );
}
